/**
 * `AppIssueSourceCard` — the app Integrations panel's inbound issue-tracker
 * binding (#1558 Slice 1b, #1826). Screen A2 of the epic mockup.
 *
 * The app selects a workspace issue-tracker connection, binds one or more
 * of its projects, maps issue types to requirement/defect (Story/Task →
 * requirement, Bug → defect resolved as suggestions from the discovered
 * types), and picks which content fields feed the Proposer. No credentials
 * — the workspace connection owns those. A read-only issue browser (with a
 * project filter) shows the classified issues once bound; import is Slice 2.
 *
 * Form-based, matching the GitHub/Slack cards on this tab (the mockup's
 * chip-based inline editing is idealized; the tab's real ethos is a form +
 * Save per card). Gated app-side to the binding tier — a non-admin simply
 * gets a 403 from the mutating/discovery calls, surfaced inline.
 *
 * Names no tracker: its name, logo, words for "project" and "issue
 * type", and filter language all come from the tracker's description. When
 * the last automatic sync failed, the summary says why in one fixed plain
 * message, per docs/mockups/2775-sync-failure-line.html.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Loader2, Check, RefreshCw } from 'lucide-react'
import { Button, Label, Badge, SearchableSelect } from '@/components/ui'
import {
  applicationsAPI,
  type AppIssueTrackerConnection,
  type IssueSourceProject,
  type IssueSourceIssueType,
  type IssueSourceField,
  type IssueSourceIssue,
  type ImportIssueRequest,
  type IssueImportStatus,
} from '@/lib/api'
import type { AppIssueSourceBinding, IssueSyncErrorKey } from '@/types'
import { ScopeFilterBuilder } from '@/components/ScopeFilterBuilder'
import { TrackerLogo } from '@/components/TrackerLogo'
import { invalidateCoverage } from '@/lib/issueCoverage'
import { getScopeFilterTranslator } from '@/lib/scopeFilter'
import {
  useTrackerDescriptors,
  descriptorFor,
  trackerNameList,
  trackerWords,
  connectionNameOf,
  signInOf,
  offersSignInChoice,
  type TrackerWords,
} from '@/hooks/useTrackerDescriptors'
import type { IssueTrackerProviderDescriptor } from '@/lib/api'

type Cls = 'requirement' | 'defect' | 'ignore'

/** Compact "x ago" for the last-synced line. */
function relTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'recently'
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  return `${Math.floor(s / 86400)} d ago`
}
const DEFAULT_CONTENT_FIELDS = ['summary', 'description']

/** Suggest a default classification for a discovered issue type by name
 * (AC#4 — resolved from discovered types at setup, stored by ID). */
function defaultClassFor(name: string): Cls {
  const n = name.trim().toLowerCase()
  if (['story', 'task', 'epic', 'requirement', 'user story'].includes(n)) return 'requirement'
  if (['bug', 'defect', 'incident'].includes(n)) return 'defect'
  return 'ignore'
}

/** The classification with a default for every type or label not yet
 * classified: a label tracker gets only its two suggestions (while
 * `suggest` is on), a type tracker a default for every type. Returns `prev`
 * itself when nothing changes. */
function withDefaults(
  prev: Record<string, Cls>,
  types: IssueSourceIssueType[],
  byLabel: boolean,
  suggest: boolean,
): Record<string, Cls> {
  const next = { ...prev }
  let changed = false
  for (const t of types) {
    if (next[t.id] !== undefined) continue
    const c = byLabel ? (suggest ? suggestedLabelClass(t.name) : undefined) : defaultClassFor(t.name)
    if (c) { next[t.id] = c; changed = true }
  }
  return changed ? next : prev
}

function seedClassification(binding: AppIssueSourceBinding | null): Record<string, Cls> {
  const out: Record<string, Cls> = {}
  for (const id of binding?.requirementIssueTypeIds ?? []) out[id] = 'requirement'
  for (const id of binding?.defectIssueTypeIds ?? []) out[id] = 'defect'
  // A label binding's mapping, in the same shape: labels and types never
  // share an id space within one binding.
  for (const id of binding?.requirementLabelIds ?? []) out[id] = 'requirement'
  for (const id of binding?.defectLabelIds ?? []) out[id] = 'defect'
  return out
}

function providerConfigValue(conn: AppIssueTrackerConnection | undefined, key: string): string {
  const v = conn?.providerConfig?.[key]
  return typeof v === 'string' ? v : ''
}

/** Where a connection reads from, for "<name> · <where>": the site's address
 * for a tracker signed in with fields, or the app install's account. */
function connectionPlace(conn: AppIssueTrackerConnection | undefined): string {
  return providerConfigValue(conn, 'baseUrl') || providerConfigValue(conn, 'account')
}

/** A label tracker has dozens of labels, so none is mapped by default except
 * the two most repositories have: `enhancement` means a requirement and
 * `bug` a defect. Offered only while the form has no saved label mapping. */
function suggestedLabelClass(name: string): Cls | undefined {
  const n = name.trim().toLowerCase()
  if (n === 'enhancement') return 'requirement'
  if (n === 'bug') return 'defect'
  return undefined
}

/** The side a label added by hand starts on: defect for a label that reads
 * like one, requirement otherwise. One click on the other side changes it. */
function addedLabelClass(name: string): Cls {
  return ['bug', 'defect', 'regression', 'incident'].includes(name.trim().toLowerCase()) ? 'defect' : 'requirement'
}

/** A body-section field's name from its id, for a saved field no recent issue
 * has any more: `section:expected behavior` → "Expected behavior". */
function sectionFieldName(id: string, prefix: string | undefined): string {
  const rest = prefix && id.startsWith(prefix) ? id.slice(prefix.length) : id.replace(/^[^:]*:/, '')
  return rest.charAt(0).toUpperCase() + rest.slice(1)
}

/** "either selected repository" / "the selected repository" / "any selected
 * repository", for a note about the selected projects. */
function whichSelected(count: number, project: string): string {
  if (count === 2) return `either selected ${project}`
  if (count > 2) return `any selected ${project}`
  return `the selected ${project}`
}

/** The fixed failure line for each sync-failure reason: what happened, then
 * what to do next. The only text the line ever shows; raw error text never
 * reaches the screen, because it can carry addresses, sign-in details or
 * issue content. `settings` renders the link to Workspace Settings. */
function syncFailureMessage(
  key: IssueSyncErrorKey,
  d: IssueTrackerProviderDescriptor,
  settings: (text: string) => React.ReactNode,
  /** The one name the message shows, when the server gave one. */
  subject?: string,
  /** For a rate limit: when sync starts again. */
  retryAt?: string,
  /** The connection signs in with a personal token on a tracker that also
   * has an app install, so the fix is the token, not the install. */
  viaToken = false,
): React.ReactNode {
  // The generic stand-in (no tracker id) reads as a phrase, not a name.
  const name = d.provider ? d.displayName : 'the issue tracker'
  // A tracker with an app install names no field to update unless the
  // connection uses a token, which is worded on its own below.
  const secret = d.credentialSource ? undefined : d.credentialSchema.find((f) => f.secret)
  switch (key) {
    case 'connection_removed':
      return <>The connection to {name} was removed. {settings(`Connect ${name} again in Workspace Settings`)}, or Edit and pick another connection.</>
    case 'sign_in_rejected':
      if (viaToken) {
        return <>The personal token for {connectionNameOf(d)} stopped working. It may have expired or been removed. {settings('Replace the token in Workspace Settings')}.</>
      }
      return <>{name} rejected the sign-in. {settings(secret ? `Update the ${secret.label} in Workspace Settings` : 'Check the connection in Workspace Settings')}.</>
    case 'unreachable':
      return <>OttoTester couldn’t reach {name}. It will try again at the next scheduled sync.</>
    case 'bad_filter':
      return <>The scope filter can’t be used because its parentheses don’t match. Edit the filter.</>
    case 'app_not_installed':
      return <>The {name} App isn’t installed on {subject ?? 'this workspace'} any more. {settings('Install it again in Workspace Settings')}.</>
    case 'rate_limited': {
      const when = retryAt ? new Date(retryAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null
      return <>{name} asked OttoTester to slow down. {when ? `Sync starts again after ${when}.` : 'Sync starts again by itself.'}</>
    }
    case 'too_many_at_once':
      return <>Some {name} issues couldn’t be read: more than 1,000 changed in the same second. Narrow the scope filter to read them.</>
    case 'project_not_visible': {
      const words = trackerWords(d)
      return viaToken
        ? <>OttoTester can no longer see {subject ?? `one of the ${words.projects}`}. Check the personal token’s {words.project} access, or remove it from this app.</>
        : <>OttoTester can no longer see {subject ?? `one of the ${words.projects}`}. Check the {name} App’s {words.project} access, or remove it from this app.</>
    }
    default:
      return <>Something went wrong syncing with {name}. It will try again at the next scheduled sync.</>
  }
}

export function AppIssueSourceCard({
  appId,
  initialBinding,
  connections,
  settingsUrl,
  onBindingChange,
}: {
  appId: string
  initialBinding: AppIssueSourceBinding | null
  connections: AppIssueTrackerConnection[]
  /** Workspace-scoped Integrations settings URL, passed from the parent panel
   * so all three "Open Workspace Settings" links share one source (#2306).
   * The card used to hardcode a relative `../../settings/integrations` that
   * dropped the workspace and landed on the wrong page. */
  settingsUrl: string
  /** Called after a save with the saved link, and after a remove with
   * null, so the app card's one-line Integrations summary updates while
   * this card is still open. */
  onBindingChange?: (binding: AppIssueSourceBinding | null) => void
}) {
  const queryClient = useQueryClient()
  const descriptors = useTrackerDescriptors()
  const [binding, setBinding] = useState<AppIssueSourceBinding | null>(initialBinding)
  const [editing, setEditing] = useState<boolean>(!initialBinding)

  // Form state (seeded from the binding; the user mutates then Saves).
  const [connectionId, setConnectionId] = useState(initialBinding?.connectionId ?? connections[0]?.id ?? '')
  const [projectKeys, setProjectKeys] = useState<string[]>(initialBinding?.projectKeys ?? [])
  const [classification, setClassification] = useState<Record<string, Cls>>(seedClassification(initialBinding))
  const [reqFields, setReqFields] = useState<string[]>(initialBinding?.requirementContentFields ?? DEFAULT_CONTENT_FIELDS)
  const [defFields, setDefFields] = useState<string[]>(initialBinding?.defectContentFields ?? DEFAULT_CONTENT_FIELDS)
  // Scope filter + automatic sync (#1867).
  const [scopeFilter, setScopeFilter] = useState<string>(initialBinding?.scopeFilter ?? '')
  const [syncEnabled, setSyncEnabled] = useState<boolean>(initialBinding?.syncEnabled ?? false)
  const [filterValid, setFilterValid] = useState<boolean>(true)
  const [userDisplayNames, setUserDisplayNames] = useState<Record<string, string>>(initialBinding?.userDisplayNames ?? {})

  // Discovery (loaded while editing).
  const [projects, setProjects] = useState<IssueSourceProject[] | null>(null)
  const [types, setTypes] = useState<IssueSourceIssueType[] | null>(null)
  const [typesLoading, setTypesLoading] = useState(false)
  const [fields, setFields] = useState<IssueSourceField[] | null>(null)
  const [discoveryError, setDiscoveryError] = useState<string | null>(null)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState(false)
  // Whether to suggest `enhancement` / `bug` for a label tracker: only while
  // the form has no saved label mapping, so editing never adds a label the
  // admin left out.
  const [suggestLabels, setSuggestLabels] = useState<boolean>(initialBinding?.classificationScheme !== 'label')

  // The tracker this card is about: while editing, the one being picked, so
  // switching trackers switches the filter builder and the words with it;
  // otherwise the saved binding's. Every name and word below comes from it.
  const activeConnection = connections.find((c) => c.id === (editing ? connectionId : (binding?.connectionId ?? connectionId)))
  // A saved binding whose connection was deleted no longer says which
  // tracker it used. When the product describes only one tracker, it was
  // that one; otherwise the generic stand-in names none.
  const descriptor = activeConnection
    ? descriptorFor(descriptors, activeConnection.provider)
    : descriptors?.length === 1 ? descriptors[0]! : descriptorFor(descriptors, undefined)
  const words = trackerWords(descriptor)
  const translator = getScopeFilterTranslator(descriptor.filterLanguage)
  // A label tracker maps labels; a type tracker maps issue types.
  const byLabel = descriptor.classificationScheme === 'label'
  // Content fields named by the issues' own headings carry this prefix. For
  // such a tracker the fields come from the selected projects' issues, so
  // discovery asks again when the selection changes. A tracker whose fields
  // are the same everywhere (Jira) is asked once per connection.
  const fieldPrefix = descriptor.strictFieldPrefix
  const fieldKeys = fieldPrefix ? projectKeys.join(',') : ''
  // What the type loader needs to apply defaults as the types arrive,
  // without reloading the types when it changes.
  const defaultsRef = useRef({ ready: false, byLabel, suggestLabels })
  defaultsRef.current = { ready: descriptors !== undefined, byLabel, suggestLabels }

  // Load projects when the connection changes (form only).
  useEffect(() => {
    if (!editing || !connectionId) return
    let cancelled = false
    setDiscoveryError(null)
    applicationsAPI.getIssueSourceProjects(appId, connectionId)
      .then((p) => { if (!cancelled) setProjects(p) })
      .catch((err) => {
        if (!cancelled) setDiscoveryError(err instanceof Error ? err.message : String(err))
      })
    return () => { cancelled = true }
  }, [appId, connectionId, editing])

  // Load the content fields: per connection, and again per selection for a
  // tracker whose fields come from its issues.
  useEffect(() => {
    if (!editing || !connectionId) return
    let cancelled = false
    setFields(null)
    applicationsAPI.getIssueSourceFields(appId, connectionId, fieldKeys ? fieldKeys.split(',') : [])
      .then((f) => { if (!cancelled) setFields(f) })
      .catch((err) => {
        if (!cancelled) setDiscoveryError(err instanceof Error ? err.message : String(err))
      })
    return () => { cancelled = true }
  }, [appId, connectionId, editing, fieldKeys])

  // Load the UNION of issue types when the selected projects change, then
  // apply name-based default classifications to any not-yet-classified type.
  useEffect(() => {
    if (!editing || !connectionId || projectKeys.length === 0) {
      setTypes([])
      setTypesLoading(false)
      return
    }
    let cancelled = false
    setTypes(null)
    setTypesLoading(true)
    applicationsAPI
      .getIssueSourceIssueTypes(appId, connectionId, projectKeys)
      .then((t) => {
        if (cancelled) return
        setTypes(t)
        // In the same update as the types, so Save never sees them without
        // their defaults. The effect below covers descriptions that arrive
        // later.
        const d = defaultsRef.current
        if (d.ready) setClassification((prev) => withDefaults(prev, t, d.byLabel, d.suggestLabels))
      })
      .catch((err) => {
        if (!cancelled) setDiscoveryError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false)
      })
    return () => { cancelled = true }
  }, [appId, connectionId, projectKeys, editing])

  // Apply the defaults to any not-yet-classified type or label, once the
  // tracker is known (a label tracker gets only its two suggestions, a type
  // tracker a default for every type). Waits for the descriptions, so a
  // label tracker is never given type defaults while they load.
  useEffect(() => {
    if (!types || !descriptors) return
    setClassification((prev) => withDefaults(prev, types, byLabel, suggestLabels))
  }, [types, descriptors, byLabel, suggestLabels])

  const toggleProject = useCallback((key: string) => {
    setProjectKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }, [])

  // Save only once the current projects' issue types have loaded — clicking
  // before then would persist an empty mapping (defaults are applied when
  // types resolve).
  const canSave = connectionId !== '' && projectKeys.length > 0 && !typesLoading && types !== null && !saving && filterValid

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    setSaveOk(false)
    try {
      // Derive the mapping from the CURRENT union of types only — so a type
      // ID left over in `classification` from a previously-selected project
      // (no longer in the union) is never sent, which would otherwise fail
      // stage-2 validation.
      const currentTypes = types ?? []
      const isMapped = (c: Cls | undefined) => c === 'requirement' || c === 'defect'
      // A label mapping keeps a mapped label no selected project has any
      // more: the server saves it as missing and the card warns, rather than
      // the mapping quietly losing it.
      let sorting: Pick<AppIssueSourceBinding, 'classificationScheme' | 'requirementIssueTypeIds' | 'defectIssueTypeIds' | 'issueTypeNames' | 'requirementLabelIds' | 'defectLabelIds' | 'labelNames'>
      if (byLabel) {
        const mapped = Object.entries(classification).filter(([, c]) => isMapped(c))
        const labelNames: Record<string, string> = {}
        for (const [id] of mapped) {
          const name = currentTypes.find((t) => t.id === id)?.name ?? binding?.labelNames?.[id]
          if (name) labelNames[id] = name
        }
        sorting = {
          classificationScheme: 'label',
          requirementLabelIds: mapped.filter(([, c]) => c === 'requirement').map(([id]) => id),
          defectLabelIds: mapped.filter(([, c]) => c === 'defect').map(([id]) => id),
          labelNames,
        }
      } else {
        // Cache id→name for the mapped types so the summary + a later rename
        // stay legible without a round-trip.
        const issueTypeNames: Record<string, string> = {}
        for (const t of currentTypes) if (isMapped(classification[t.id])) issueTypeNames[t.id] = t.name
        sorting = {
          classificationScheme: 'issueType',
          requirementIssueTypeIds: currentTypes.filter((t) => classification[t.id] === 'requirement').map((t) => t.id),
          defectIssueTypeIds: currentTypes.filter((t) => classification[t.id] === 'defect').map((t) => t.id),
          issueTypeNames,
        }
      }
      const patch: AppIssueSourceBinding = {
        connectionId,
        projectKeys,
        ...sorting,
        requirementContentFields: reqFields,
        defectContentFields: defFields,
        ...(scopeFilter.trim() ? { scopeFilter: scopeFilter.trim() } : {}),
        ...(Object.keys(userDisplayNames).length > 0 ? { userDisplayNames } : {}),
        syncEnabled,
      }
      const res = await applicationsAPI.updateIntegrations(appId, { issueSource: patch })
      // The connection and type mapping decide which issues count as
      // requirements or defects, so the dashboard must ask again.
      void invalidateCoverage(queryClient)
      setBinding(res.bindings.issueSource)
      onBindingChange?.(res.bindings.issueSource)
      setSuggestLabels(false)
      setSaveOk(true)
      setEditing(false)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't save — try again.")
    } finally {
      setSaving(false)
    }
  }

  async function handleUnbind() {
    setSaving(true)
    setSaveError(null)
    try {
      await applicationsAPI.updateIntegrations(appId, { issueSource: null })
      void invalidateCoverage(queryClient)
      setBinding(null)
      onBindingChange?.(null)
      setProjectKeys([])
      setClassification({})
      setEditing(true)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't remove — try again.")
    } finally {
      setSaving(false)
    }
  }


  // Nothing is connected, so name every tracker that could be.
  const trackerNames = trackerNameList(descriptors)
  const onboarding = (
    <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
      <strong className="block text-foreground mb-1">No issue tracker is connected on this workspace yet.</strong>
      Connect {trackerNames} on the workspace to pull requirements and defects into this app.{' '}
      <Link to={settingsUrl} className="text-primary inline-flex items-center gap-1 hover:underline">
        Open Workspace Settings <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  )

  // ── Empty state: no workspace connection, and nothing saved yet ──
  // A saved binding still shows its summary, so a deleted connection's
  // failure line and the Remove action stay reachable.
  if (connections.length === 0 && !binding) {
    return <CardShell descriptor={descriptors?.[0]}>{onboarding}</CardShell>
  }

  // A field named by a heading has an id made from that name, so the id
  // adds nothing; a tracker's own field codes are shown to tell apart two
  // fields with one name.
  const fieldOptions = (fields ?? []).map((f) => ({ value: f.id, label: fieldPrefix ? f.name : `${f.name} (${f.id})` }))
  const baseFieldNames = (fields ?? []).filter((f) => !fieldPrefix || !f.id.startsWith(fieldPrefix)).map((f) => f.name)
  const selectedProjectNames = projectKeys.map((k) => projects?.find((p) => p.key === k)?.name ?? binding?.projectNames?.[k] ?? k)
  // Mapped requirement + defect type ids — the scope-filter preview's scope.
  const mappedTypeIds = (types ?? [])
    .filter((t) => classification[t.id] === 'requirement' || classification[t.id] === 'defect')
    .map((t) => t.id)

  return (
    <CardShell descriptor={descriptor} badge={binding ? <Badge variant="default">Linked</Badge> : <Badge variant="outline">Not linked</Badge>}>
      {saveError && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive" role="alert">{saveError}</div>}
      {saveOk && !editing && <div className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-2 py-1 text-xs text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300"><Check className="h-3 w-3" /> Saved</div>}

      {!editing && binding ? (
        <BoundSummary
          binding={binding}
          connection={connections.find((c) => c.id === binding.connectionId)}
          descriptor={descriptor}
          settingsUrl={settingsUrl}
          onEdit={() => {
            // A saved connection that was deleted can't be edited against;
            // start the form on one that still exists.
            if (!connections.some((c) => c.id === connectionId) && connections[0]) setConnectionId(connections[0].id)
            setEditing(true)
            setSaveOk(false)
          }}
          onUnbind={handleUnbind}
          unbinding={saving}
        />
      ) : (
        connections.length === 0 ? (
          // Editing a binding whose connection is gone, with none to pick.
          <div className="space-y-3">
            {onboarding}
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : (
        <div className="space-y-4">
          {/* Connection */}
          <div className="space-y-1">
            <Label htmlFor="issue-source-connection">Connection</Label>
            {connections.length === 1 ? (
              <div className="text-sm flex items-center gap-2">
                <span className="font-medium">{connectionNameOf(descriptorFor(descriptors, connections[0]!.provider))}</span>
                <span className="text-muted-foreground">{connectionPlace(connections[0])}</span>
              </div>
            ) : (
              <SearchableSelect
                id="issue-source-connection"
                value={connectionId}
                onChange={(v) => {
                  setConnectionId(v); setProjectKeys([]); setClassification({}); setSuggestLabels(true); setScopeFilter(''); setFilterValid(true); setUserDisplayNames({})
                  // Another tracker's fields mean nothing here, and a kept one
                  // would stop a section-only mapping from being section-only.
                  // Start from title and body, as a new connection does.
                  if (v !== connectionId) { setReqFields(DEFAULT_CONTENT_FIELDS); setDefFields(DEFAULT_CONTENT_FIELDS) }
                }}
                options={connections.map((c) => ({ value: c.id, label: `${connectionNameOf(descriptorFor(descriptors, c.provider))} · ${connectionPlace(c)}` }))}
                placeholder="Select a connection"
                aria-label="Issue-tracker connection"
              />
            )}
          </div>

          {/* Projects */}
          <div className="space-y-1.5">
            <Label>{words.Projects}</Label>
            {projects === null ? (
              <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading {words.projects}…</div>
            ) : projects.length === 0 ? (
              <p className="text-xs text-muted-foreground">No {words.projects} are visible with this connection.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {projects.map((p) => {
                  const on = projectKeys.includes(p.key)
                  return (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => toggleProject(p.key)}
                      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs ${on ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}
                      aria-pressed={on}
                    >
                      {on && <Check className="h-3 w-3" />}
                      {/* A key that is the project's id (a repository's
                          number) means nothing to read, so its name stands
                          alone; a readable key (Jira's SHOP) comes first. */}
                      {p.key === p.id ? (
                        <span className="font-medium">{p.name}</span>
                      ) : (
                        <>
                          <span className="font-medium">{p.key}</span>
                          <span className="text-muted-foreground">{p.name}</span>
                        </>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Pick one or more. Issues from all of them show together.</p>
          </div>

          {/* Type mapping */}
          {projectKeys.length > 0 && (
            <div className="space-y-1.5">
              <Label>Which {words.issueTypes} are requirements vs defects?</Label>
              {types === null ? (
                <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading {words.issueTypes}…</div>
              ) : byLabel ? (
                <LabelMapping
                  labels={types}
                  classification={classification}
                  onChange={setClassification}
                  savedNames={binding?.labelNames}
                  selectedProjectNames={selectedProjectNames}
                  words={words}
                  trackerName={descriptor.displayName}
                />
              ) : types.length === 0 ? (
                <p className="text-xs text-muted-foreground">No {words.issueTypes} found in the selected {words.projects}.</p>
              ) : (
                <div className="space-y-1.5">
                  {types.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 text-sm">
                      <span className="flex-1 truncate">{t.name}</span>
                      <ClassPicker value={classification[t.id] ?? 'ignore'} onChange={(c) => setClassification((prev) => ({ ...prev, [t.id]: c }))} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Content fields */}
          {projectKeys.length > 0 && (
            <div className="space-y-1.5">
              <div className="grid gap-3 sm:grid-cols-2">
                <FieldPicker label="Requirement fields to import" selected={reqFields} onChange={setReqFields} options={fieldOptions} idPrefix="req" fieldPrefix={fieldPrefix} loaded={fields !== null} />
                <FieldPicker label="Defect fields to import" selected={defFields} onChange={setDefFields} options={fieldOptions} idPrefix="def" fieldPrefix={fieldPrefix} loaded={fields !== null} />
              </div>
              {fieldPrefix && (
                <p className="text-xs text-muted-foreground">
                  Fields are the <span className="font-mono">##</span> headings found in each {words.project}’s recent issues
                  {baseFieldNames.length > 0 ? `, plus ${baseFieldNames.join(' and ')}` : ''}. Only the chosen sections are sent for
                  suggestions. Headings with a date in them, like a signed note, aren’t offered.
                </p>
              )}
            </div>
          )}

          {/* Scope filter + automatic sync (#1867) */}
          {projectKeys.length > 0 && connectionId && (
            <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
              {translator ? (
                <ScopeFilterBuilder
                  key={connectionId}
                  translator={translator}
                  projectsWord={words.projects}
                  appId={appId}
                  connectionId={connectionId}
                  projectKeys={projectKeys}
                  issueTypeIds={mappedTypeIds}
                  value={scopeFilter}
                  onChange={setScopeFilter}
                  onValidityChange={setFilterValid}
                  initialUserNames={userDisplayNames}
                  onUserNamesChange={setUserDisplayNames}
                  classifierWord={byLabel ? words.issueType : undefined}
                />
              ) : (
                // No visual builder for this tracker's filter language: the
                // filter is typed as plain text and checked on save.
                <div className="space-y-1.5">
                  <Label htmlFor="issue-source-filter-text">Which issues should be in play?</Label>
                  <textarea
                    id="issue-source-filter-text"
                    value={scopeFilter}
                    onChange={(e) => setScopeFilter(e.target.value)}
                    rows={2}
                    placeholder={`Leave empty to include every mapped type in the ${words.projects} above.`}
                    className="w-full rounded-md border border-border bg-background px-2.5 py-2 font-mono text-xs"
                  />
                </div>
              )}
              <div className="flex items-center gap-2.5 border-t border-border pt-2.5">
                <button
                  type="button"
                  role="switch"
                  aria-checked={syncEnabled}
                  aria-label="Keep in sync automatically"
                  onClick={() => setSyncEnabled((v) => !v)}
                  className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${syncEnabled ? 'bg-primary' : 'bg-border'}`}
                >
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${syncEnabled ? 'left-[18px]' : 'left-0.5'}`} />
                </button>
                <div>
                  <span className="text-sm font-medium">Keep in sync automatically</span>
                  <p className="text-[11px] text-muted-foreground">Otto checks this tracker on a schedule and refreshes the proposed tests as issues change.</p>
                </div>
              </div>
            </div>
          )}

          {discoveryError && <p className="text-xs text-amber-700 dark:text-amber-300">Couldn’t reach the tracker: {discoveryError}</p>}

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => void handleSave()} disabled={!canSave}>
              {saving ? 'Saving…' : binding ? 'Save changes' : 'Save'}
            </Button>
            {binding && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            )}
          </div>
        </div>
        )
      )}

      {/* Read-only browser — only once bound. */}
      {binding && !editing && (
        <IssueSourceBrowser appId={appId} projectKeys={binding.projectKeys} words={words} binding={binding} fieldPrefix={fieldPrefix} />
      )}
    </CardShell>
  )
}

// ── Card shell (matches the GitHub/Slack section cards on this tab) ────────
// `badge` is optional — the empty/onboarding state (no workspace tracker to
// link to) shows no status badge, since "Not linked" would mislead when
// there's nothing to link.
function CardShell({
  children, badge, descriptor,
}: {
  children: React.ReactNode
  badge?: React.ReactNode
  /** The tracker whose tile heads the card; none while nothing is known. */
  descriptor?: IssueTrackerProviderDescriptor
}) {
  return (
    <section aria-labelledby="issue-source-heading" className="rounded-lg border bg-card p-4">
      <header className="flex items-center gap-3 mb-3">
        <TrackerLogo logoKey={descriptor?.logoKey} name={descriptor?.displayName ?? 'Issue tracker'} className="h-8 w-8 text-sm" />
        <h3 id="issue-source-heading" className="text-sm font-semibold flex-1">Issue tracker</h3>
        {badge}
      </header>
      {children}
    </section>
  )
}

// ── 3-way classification picker ────────────────────────────────────────────
function ClassPicker({
  value, onChange, withIgnore = true,
}: {
  value: Cls
  onChange: (c: Cls) => void
  /** A label row has no Ignore: leaving a label out is the same thing. */
  withIgnore?: boolean
}) {
  const opts: Array<{ v: Cls; label: string }> = [
    { v: 'requirement', label: 'Requirement' },
    { v: 'defect', label: 'Defect' },
    ...(withIgnore ? [{ v: 'ignore' as const, label: 'Ignore' }] : []),
  ]
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      {opts.map((o) => (
        <button
          type="button"
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-2.5 py-1 text-xs ${value === o.v ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
          aria-pressed={value === o.v}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ── Label mapping (a tracker that sorts by label) ─────────────────────────
/**
 * The label rows of docs/mockups/2776-github-issues.html section B. A
 * repository can have dozens of labels, so only the mapped ones are listed,
 * each with Requirement / Defect and a × to remove it, plus a search to add
 * another. There is no Ignore: a label left out is ignored. A mapped label no
 * selected repository has any more stays listed, with a note, so the admin
 * decides what replaces it.
 */
function LabelMapping({
  labels, classification, onChange, savedNames, selectedProjectNames, words, trackerName,
}: {
  /** The labels across the selected projects. */
  labels: IssueSourceIssueType[]
  classification: Record<string, Cls>
  onChange: (next: Record<string, Cls>) => void
  /** Names saved with the binding, for a mapped label no project has now. */
  savedNames?: Record<string, string>
  selectedProjectNames: string[]
  words: TrackerWords
  trackerName: string
}) {
  const mappedIds = Object.keys(classification).filter((id) => classification[id] === 'requirement' || classification[id] === 'defect')
  const nameOf = (id: string) => labels.find((l) => l.id === id)?.name ?? savedNames?.[id] ?? id
  const unmapped = labels.filter((l) => !mappedIds.includes(l.id))
  const set = (id: string, c: Cls) => onChange({ ...classification, [id]: c })
  return (
    <div className="space-y-1.5">
      {mappedIds.length === 0 && (
        <p className="text-xs text-muted-foreground">No {words.issueTypes} picked yet. Add one below.</p>
      )}
      {mappedIds.map((id) => {
        const label = labels.find((l) => l.id === id)
        const name = nameOf(id)
        return (
          <div key={id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="flex flex-1 min-w-[140px] items-center gap-2 truncate">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full border border-border"
                style={{ background: label?.color ? `#${label.color}` : '#cccccc' }}
                aria-hidden="true"
              />
              {name}
            </span>
            <ClassPicker value={classification[id]!} onChange={(c) => set(id, c)} withIgnore={false} />
            <button
              type="button"
              onClick={() => set(id, 'ignore')}
              aria-label={`Remove ${name}`}
              className="text-muted-foreground hover:text-foreground"
            >
              ×
            </button>
            {!label && (
              <span className="basis-full text-[11px] text-amber-700 dark:text-amber-400">
                Not found in {whichSelected(selectedProjectNames.length, words.project)}. It was renamed or deleted on {trackerName}.
              </span>
            )}
          </div>
        )
      })}
      <SearchableSelect
        id="issue-source-label-add"
        value=""
        onChange={(v) => {
          const added = labels.find((l) => l.id === v)
          if (added) set(added.id, addedLabelClass(added.name))
        }}
        options={unmapped.map((l) => ({ value: l.id, label: l.name }))}
        placeholder={`Add a ${words.issueType}… (search ${selectedProjectNames.join(', ')})`}
        searchPlaceholder={`Search ${words.issueTypes}…`}
        aria-label={`Add a ${words.issueType}`}
        disabled={unmapped.length === 0}
      />
      <p className="text-xs text-muted-foreground">
        Issues with a requirement {words.issueType} become requirements. Issues with a defect {words.issueType} become
        defects. Issues with neither are left out. “enhancement” and “bug” are suggested when the {words.projects} have them.
      </p>
    </div>
  )
}

// ── Content-field chip picker (add via SearchableSelect, remove via chip) ──
function FieldPicker({
  label, selected, onChange, options, idPrefix, fieldPrefix, loaded,
}: {
  label: string
  selected: string[]
  onChange: (next: string[]) => void
  options: Array<{ value: string; label: string }>
  idPrefix: string
  /** The prefix of fields named by the issues' own headings, if any. */
  fieldPrefix?: string
  /** Whether the field list has loaded, so a missing field can be told
   * apart from one not loaded yet. */
  loaded: boolean
}) {
  const isSection = (id: string) => Boolean(fieldPrefix && id.startsWith(fieldPrefix))
  const labelFor = (id: string) => options.find((o) => o.value === id)?.label ?? (isSection(id) ? sectionFieldName(id, fieldPrefix) : id)
  // A saved section no recent issue has stays selected, and says so.
  const notSeen = (id: string) => loaded && isSection(id) && !options.some((o) => o.value === id)
  const remaining = options.filter((o) => !selected.includes(o.value))
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-field-add`}>{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {selected.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs">
            {labelFor(id)}
            {notSeen(id) && <span className="text-[11px] text-amber-700 dark:text-amber-400">· not seen in recent issues</span>}
            <button type="button" onClick={() => onChange(selected.filter((s) => s !== id))} aria-label={`Remove ${labelFor(id)}`} className="text-muted-foreground hover:text-foreground">×</button>
          </span>
        ))}
      </div>
      <SearchableSelect
        id={`${idPrefix}-field-add`}
        value=""
        onChange={(v) => { if (v) onChange([...selected, v]) }}
        options={remaining}
        placeholder="Add a field…"
        searchPlaceholder="Search fields…"
        aria-label={label}
        disabled={remaining.length === 0}
      />
    </div>
  )
}

// ── Bound summary (compact, when not editing) ──────────────────────────────
function BoundSummary({
  binding, connection, descriptor, settingsUrl, onEdit, onUnbind, unbinding,
}: {
  binding: AppIssueSourceBinding
  connection: AppIssueTrackerConnection | undefined
  descriptor: IssueTrackerProviderDescriptor
  settingsUrl: string
  onEdit: () => void
  onUnbind: () => void
  unbinding: boolean
}) {
  // A label tracker's mapping is labels; a type tracker's is issue types.
  const byLabel = binding.classificationScheme === 'label'
  const names = (byLabel ? binding.labelNames : binding.issueTypeNames) ?? {}
  const reqIds = (byLabel ? binding.requirementLabelIds : binding.requirementIssueTypeIds) ?? []
  const defIds = (byLabel ? binding.defectLabelIds : binding.defectIssueTypeIds) ?? []
  const reqNames = reqIds.map((id) => names[id] ?? id)
  const defNames = defIds.map((id) => names[id] ?? id)
  const missingLabels = byLabel ? (binding.missingLabelIds ?? []) : []
  const place = connectionPlace(connection)
  const words = trackerWords(descriptor)
  const failure = binding.lastSyncError
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Connection</span>
        <span className="font-medium">{connection ? connectionNameOf(descriptor) : 'connection'}</span>
        {place && <span className="text-muted-foreground">· {place}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <span className="text-muted-foreground text-xs mr-1 self-center">{words.Projects}</span>
        {binding.projectKeys.map((k) => (
          <span key={k} className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium">{binding.projectNames?.[k] ?? k}</span>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Requirements:</span> {reqNames.length ? reqNames.join(', ') : '—'}
        {'  ·  '}
        <span className="font-medium text-foreground">Defects:</span> {defNames.length ? defNames.join(', ') : '—'}
      </div>
      {missingLabels.map((id) => (
        // A mapped label the tracker no longer has, found on save and after
        // each sync. Issues that had it stop sorting under its heading.
        <div key={id} role="status" className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          The label “{names[id] ?? id}” isn’t in {binding.projectKeys.length === 2 ? `either ${words.project}` : binding.projectKeys.length > 2 ? `any of the ${words.projects}` : `the ${words.project}`} any more.
          {' '}Issues that had it aren’t being sorted as {reqIds.includes(id) ? 'requirements' : 'defects'}. Edit to pick another label.
        </div>
      ))}
      {binding.scopeFilter && (
        <div className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Filter:</span> <span className="font-mono">{binding.scopeFilter}</span>
        </div>
      )}
      <div className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Automatic sync:</span>{' '}
        {binding.syncEnabled
          ? binding.lastSyncedAt
            ? `on · last synced ${relTime(binding.lastSyncedAt)}`
            : 'on · not run yet'
          : 'off'}
      </div>
      {failure && (
        // Why automatic sync stopped. "Last synced" above still shows
        // the last sync that worked; this line sits right under it so the two
        // read together, until a sync succeeds.
        <div role="status" className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" aria-hidden="true" />
          <span>
            {/* A rate limit is a pause, not a failure: sync starts again by itself. */}
            {failure.messageKey === 'rate_limited' ? 'Last sync paused' : 'Last sync failed'}{' '}
            <span className="text-muted-foreground">{relTime(failure.at)}</span>.{' '}
            {syncFailureMessage(failure.messageKey, descriptor, (text) => (
              <Link to={settingsUrl} className="font-medium underline">{text}</Link>
            ), failure.subject, failure.retryAt,
            Boolean(connection) && offersSignInChoice(descriptor) && signInOf(descriptor, connection) === 'token')}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={onEdit}>Edit</Button>
        <Button size="sm" variant="ghost" onClick={onUnbind} disabled={unbinding} className="text-destructive hover:bg-destructive/10">
          {unbinding ? 'Removing…' : 'Remove'}
        </Button>
      </div>
    </div>
  )
}

// ── Read-only issue browser ────────────────────────────────────────────────
/** Plain-language wording for each attachment-skip reason (#1843 Slice 2b).
 * The backend stores the internal code; the operator reads this. */
const SKIP_REASON_LABELS: Record<string, string> = {
  'no-image-capability': "the selected model can't read images",
  'unsupported-format': 'unsupported file type',
  'no-extractable-text': 'no readable text (scanned or locked PDF)',
  'download-failed': "couldn't download",
  'cap-count': 'too many attachments',
  'cap-bytes': 'attachment too large',
}

/** Group skips by reason → counted, plain-language lines. An unrecognized
 * reason (e.g. a new backend code) falls back to a generic plain phrase —
 * never the raw internal code, so no jargon leaks to the operator. */
function groupSkips(skips: Array<{ filename: string; reason: string }>): Array<{ count: number; label: string }> {
  const counts = new Map<string, number>()
  for (const s of skips) counts.set(s.reason, (counts.get(s.reason) ?? 0) + 1)
  return [...counts.entries()].map(([reason, count]) => ({
    count,
    label: SKIP_REASON_LABELS[reason] ?? "couldn't be used",
  }))
}

/** Per-issue import-status pill, with the attachment-skip note (#1843
 * Slice 2b) beneath it when the import couldn't use some attachments. */
function ImportStatusBadge({ s, fieldPrefix }: { s: IssueImportStatus | undefined; fieldPrefix?: string }) {
  if (!s) return null
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: 'Queued', cls: 'bg-muted text-muted-foreground' },
    inFlight: { label: 'Importing…', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
    sent: { label: 'Imported', cls: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' },
    dead: { label: 'Failed', cls: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300' },
  }
  // A finished job that left the issue out because of how it is labeled.
  const skipped = s.status === 'sent' && s.importResult === 'skipped'
  const m = skipped
    ? { label: 'Skipped', cls: 'bg-muted text-muted-foreground' }
    : (map[s.status] ?? { label: s.status, cls: 'bg-muted text-muted-foreground' })
  const skips = s.attachmentSkips ?? []
  return (
    <div>
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.cls}`} title={s.failureReason}>
        {m.label}
      </span>
      {skipped && s.skipReason && (
        <div className="mt-1 text-[10px] text-muted-foreground">
          {s.skipReason.charAt(0).toUpperCase() + s.skipReason.slice(1)}
        </div>
      )}
      {s.status === 'sent' && (s.emptySectionFieldIds?.length ?? 0) > 0 && (
        // Imported, but every mapped section was missing or empty, so there
        // was nothing to suggest tests from. The issue still counts as
        // uncovered.
        <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-500">
          No {s.emptySectionFieldIds!.map((id) => `“${sectionFieldName(id, fieldPrefix)}”`).join(' or ')} section, so no tests suggested
        </div>
      )}
      {skips.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {groupSkips(skips).map((g, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px] text-amber-700 dark:text-amber-500">
              <span className="h-1 w-1 rounded-full bg-amber-500 shrink-0" aria-hidden="true" />
              {g.count} skipped — {g.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** How a label binding sorted an issue: "Requirement" or "Defect" with the
 * label that decided it underneath, or "Both labels" for one the import
 * skips because it has a label on each side. */
function SortedAs({ issue, binding }: { issue: IssueSourceIssue; binding?: AppIssueSourceBinding | null }) {
  if (issue.sortedAs === 'conflict') return <>Both labels</>
  if (issue.sortedAs !== 'requirement' && issue.sortedAs !== 'defect') return null
  const ids = issue.sortedAs === 'requirement' ? binding?.requirementLabelIds : binding?.defectLabelIds
  const decider = issue.labels?.find((l) => ids?.includes(l.id))
  return (
    <>
      {issue.sortedAs === 'requirement' ? 'Requirement' : 'Defect'}
      {decider && <span className="mt-0.5 block text-[10px] text-muted-foreground">{decider.name}</span>}
    </>
  )
}

/**
 * Browse the bound projects' issues and import them as candidates.
 *
 * Exported since #2158 slice 2 so the Proposed tab can host it. Only the
 * IMPORT half moves: the binding itself (which connection, which projects,
 * how issues are classified) stays on the integrations surface, because two
 * places to configure one binding is the duplicate-path failure the review
 * checklist names first. Importing is an action against an existing binding,
 * which is a different thing from defining one.
 *
 * Needs nothing but the app and the bound project keys, which is what makes
 * the split clean.
 */
export function IssueSourceBrowser({
  appId,
  projectKeys,
  words = trackerWords({ projectNoun: 'project', issueTypeNoun: 'issue type' }),
  binding,
  fieldPrefix,
  emptyMessage,
}: {
  appId: string
  projectKeys: string[]
  /** The tracker's words, for the project filter. Generic when the caller
   * doesn't know the tracker. */
  words?: TrackerWords
  /** The saved binding, when the caller has it: the projects' names, and
   * which labels decide a label tracker's sorting. */
  binding?: AppIssueSourceBinding | null
  /** The prefix of fields named by the issues' own headings, for the note on
   * an import that found none of its sections. */
  fieldPrefix?: string
  /** What to say when there are no issues to show. Without it, the text
   * points at the sorting controls above, which only this card has. The
   * Proposed tab passes its own, with a link to this card. */
  emptyMessage?: ReactNode
}) {
  const [filter, setFilter] = useState<string>('') // '' = all bound projects
  const [issues, setIssues] = useState<IssueSourceIssue[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [statuses, setStatuses] = useState<Record<string, IssueImportStatus>>({})
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const load = useCallback(async (reset: boolean) => {
    setLoading(true)
    setError(null)
    try {
      const page = await applicationsAPI.browseIssueSourceIssues(appId, {
        projectKey: filter || undefined,
        cursor: reset ? undefined : cursor,
        limit: 25,
      })
      setIssues((prev) => (reset ? page.issues : [...prev, ...page.issues]))
      setCursor(page.nextCursor)
      // How each listed issue's last import went, so an issue imported
      // earlier shows it (and any note about it) without importing again.
      // Best effort: the list stands without it.
      const ids = page.issues.map((i) => i.externalId)
      if (ids.length > 0) {
        applicationsAPI.getIssueSourceImportStatus(appId, ids)
          .then((list) => setStatuses((prev) => {
            const n = { ...prev }
            for (const st of list) n[st.externalId] = st
            return n
          }))
          .catch(() => {})
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load issues')
    } finally {
      setLoading(false)
    }
  }, [appId, filter, cursor])

  // Reload from the top whenever the project filter changes; clear the
  // selection so "Import selected (N)" never counts now-hidden rows.
  useEffect(() => {
    setSelected(new Set())
    void load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  // A label tracker's issues have no type, so the column says how the
  // binding sorted each one instead. Without the binding, the issues say:
  // they carry a sorting and no type.
  const byLabel = binding
    ? binding.classificationScheme === 'label'
    : issues.length > 0 && issues.every((i) => i.sortedAs !== undefined && !i.type.id)
  const allVisibleSelected = issues.length > 0 && issues.every((i) => selected.has(i.externalId))
  const toggleAll = () =>
    setSelected((prev) => {
      const n = new Set(prev)
      if (issues.every((i) => n.has(i.externalId))) for (const i of issues) n.delete(i.externalId)
      else for (const i of issues) n.add(i.externalId)
      return n
    })

  // Poll import status until the queued issues settle (sent/dead) or a cap.
  // First fetch is immediate (snappy feedback), then every 2s. Bail early
  // if no status rows ever materialize (nothing to wait on).
  //
  // An imported issue counts toward coverage once it is sent, so each poll
  // that sees another one sent asks for coverage again, without waiting for
  // the whole batch. One that fails changes no number and is not counted.
  // An import still pending when the polling stops is counted the next time
  // a coverage screen opens, since that read is never treated as fresh.
  const pollStatus = useCallback(async (externalIds: string[]) => {
    let sentSeen = 0
    for (let i = 0; i < 12; i++) {
      if (i > 0) await new Promise((r) => setTimeout(r, 2000))
      let list: IssueImportStatus[]
      try {
        list = await applicationsAPI.getIssueSourceImportStatus(appId, externalIds)
      } catch {
        continue
      }
      setStatuses((prev) => {
        const n = { ...prev }
        for (const s of list) n[s.externalId] = s
        return n
      })
      const sent = list.filter((s) => externalIds.includes(s.externalId) && s.status === 'sent').length
      if (sent > sentSeen) {
        sentSeen = sent
        void invalidateCoverage(queryClient)
      }
      const settled = (id: string) => {
        const s = list.find((x) => x.externalId === id)
        return s?.status === 'sent' || s?.status === 'dead'
      }
      if (externalIds.every(settled)) break
      // No rows appeared after a few tries — stop rather than spin the full
      // window (a rejected/deduped enqueue never produces a row here).
      if (list.length === 0 && i >= 2) break
    }
  }, [appId, queryClient])

  async function handleImport() {
    const toImport = issues.filter((i) => selected.has(i.externalId))
    if (toImport.length === 0) return
    setImporting(true)
    setImportError(null)
    try {
      const req: ImportIssueRequest[] = toImport.map((i) => ({
        externalId: i.externalId, displayKey: i.displayKey, projectId: i.projectId,
        projectKey: i.projectKey, issueType: i.type.id, revisionToken: i.revisionToken,
        ...(i.readHandle ? { readHandle: i.readHandle } : {}),
        ...(i.labels ? { issueLabelIds: i.labels.map((l) => l.id) } : {}),
      }))
      const { results } = await applicationsAPI.importIssueSourceIssues(appId, req)
      setStatuses((prev) => {
        const n = { ...prev }
        for (const r of results) {
          n[r.externalId] = r.result === 'rejected' || r.result === 'enqueue-failed'
            ? { externalId: r.externalId, status: 'dead', failureReason: r.reason, attempts: 0, updatedAt: '' }
            : { externalId: r.externalId, status: 'pending', attempts: 0, updatedAt: '' }
        }
        return n
      })
      setSelected(new Set())
      const queued = results.filter((r) => r.result === 'enqueued' || r.result === 'already-queued').map((r) => r.externalId)
      if (queued.length > 0) void pollStatus(queued)
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Failed to start the import')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="mt-5 border-t pt-4">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Issues in this app</h4>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => void handleImport()} disabled={importing || selected.size === 0}>
            {importing ? 'Starting…' : `Import selected${selected.size > 0 ? ` (${selected.size})` : ''}`}
          </Button>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            aria-label={`Filter by ${words.project}`}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">All {words.projects}</option>
            {projectKeys.map((k) => <option key={k} value={k}>{binding?.projectNames?.[k] ?? k}</option>)}
          </select>
          <button type="button" onClick={() => void load(true)} aria-label="Refresh" className="text-muted-foreground hover:text-foreground"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
      {importError && <p className="text-xs text-destructive" role="alert">{importError}</p>}

      {issues.length === 0 && !loading && !error ? (
        <p className="text-xs text-muted-foreground py-2">
          {emptyMessage ?? 'No classified issues yet. Map at least one requirement or defect type above to see issues here.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 pr-2 w-6">
                  <input type="checkbox" aria-label="Select all issues" checked={allVisibleSelected} onChange={toggleAll} />
                </th>
                <th className="py-1.5 pr-3 font-medium">Key</th>
                <th className="py-1.5 pr-3 font-medium">Summary</th>
                <th className="py-1.5 pr-3 font-medium">{byLabel ? 'Sorted as' : 'Type'}</th>
                <th className="py-1.5 pr-3 font-medium">Status</th>
                <th className="py-1.5 font-medium">Import</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((i) => (
                <tr key={i.externalId} className="border-b border-border/60">
                  <td className="py-1.5 pr-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${i.displayKey}`}
                      checked={selected.has(i.externalId)}
                      onChange={() => toggle(i.externalId)}
                    />
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-primary whitespace-nowrap">{i.displayKey}</td>
                  <td className="py-1.5 pr-3">{i.summary}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">
                    {byLabel ? <SortedAs issue={i} binding={binding} /> : i.type.name}
                  </td>
                  <td className="py-1.5 pr-3 whitespace-nowrap text-muted-foreground">
                    {i.status}
                    {/* Closed as not to be built: listed, but not counted. */}
                    {i.notPlanned && <span className="mt-0.5 block text-[10px]">Left out of coverage</span>}
                  </td>
                  <td className="py-1.5 whitespace-nowrap"><ImportStatusBadge s={statuses[i.externalId]} fieldPrefix={fieldPrefix} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-2 flex items-center gap-3">
        {loading && <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>}
        {cursor && !loading && (
          <Button size="sm" variant="outline" onClick={() => void load(false)}>Load more</Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Imported issues become tests under this app's <strong>Proposed</strong> tab.</p>
    </div>
  )
}
